import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { getDashboardMetrics, getDashboardNextActions } from "../api/dashboard.api";
import { isFeatureEnabled, usePlatformContext } from "./PlatformContext";
import { isUiFeatureEnabled } from "../utils/featureControl";
import { isUnauthorizedError } from "../api/http";
import { logActivity } from "../utils/activity";

const DashboardContext = createContext(null);

let cachedMetrics = null;
let cachedNextActions = null;
let cachedError = null;
let inflightMetrics = null;
let inflightNextActions = null;
let cachedAuthError = false;

export function DashboardProvider({ children }) {
  const { features, isLoading: platformLoading } = usePlatformContext();
  const dashboardEnabled = isFeatureEnabled(features, "dashboard");
  const uiDashboardEnabled = isUiFeatureEnabled("dashboard");
  const [metrics, setMetrics] = useState(() => cachedMetrics);
  const [nextActions, setNextActions] = useState(() => cachedNextActions || []);
  const [error, setError] = useState(() => cachedError);
  const [loadingMetrics, setLoadingMetrics] = useState(() => !cachedMetrics);
  const [loadingNextActions, setLoadingNextActions] = useState(() => !cachedNextActions);

  const fetchMetrics = useCallback(async ({ force = false } = {}) => {
    if (!dashboardEnabled || !uiDashboardEnabled || platformLoading) {
      setLoadingMetrics(false);
      setMetrics(null);
      setError(null);
      return null;
    }
    if (cachedAuthError) {
      setLoadingMetrics(false);
      setError(cachedError || new Error("AUTH_REQUIRED"));
      return Promise.reject(cachedError || new Error("AUTH_REQUIRED"));
    }
    if (cachedMetrics && !force) {
      setLoadingMetrics(false);
      return cachedMetrics;
    }
    if (inflightMetrics && !force) {
      return inflightMetrics;
    }
    setLoadingMetrics(true);
    inflightMetrics = getDashboardMetrics()
      .then((data) => {
        cachedMetrics = data;
        cachedError = null;
        setMetrics(data);
        setError(null);
        return data;
      })
      .catch((err) => {
        if (isUnauthorizedError(err)) {
          cachedAuthError = true;
        }
        cachedError = err;
        setMetrics(null);
        setError(err);
        logActivity("api_error", { module: "dashboard_metrics" });
        throw err;
      })
      .finally(() => {
        inflightMetrics = null;
        setLoadingMetrics(false);
      });
    return inflightMetrics;
  }, [dashboardEnabled, platformLoading, uiDashboardEnabled]);

  const fetchNextActions = useCallback(async ({ force = false } = {}) => {
    if (!dashboardEnabled || !uiDashboardEnabled || platformLoading) {
      setLoadingNextActions(false);
      setNextActions([]);
      setError(null);
      return [];
    }
    if (cachedAuthError) {
      setLoadingNextActions(false);
      setError(cachedError || new Error("AUTH_REQUIRED"));
      return Promise.reject(cachedError || new Error("AUTH_REQUIRED"));
    }
    if (cachedNextActions && !force) {
      setLoadingNextActions(false);
      return cachedNextActions;
    }
    if (inflightNextActions && !force) {
      return inflightNextActions;
    }
    setLoadingNextActions(true);
    inflightNextActions = getDashboardNextActions()
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        cachedNextActions = items;
        cachedError = null;
        setNextActions(items);
        setError(null);
        return items;
      })
      .catch((err) => {
        if (isUnauthorizedError(err)) {
          cachedAuthError = true;
        }
        cachedError = err;
        setNextActions([]);
        setError(err);
        logActivity("api_error", { module: "dashboard_next_actions" });
        throw err;
      })
      .finally(() => {
        inflightNextActions = null;
        setLoadingNextActions(false);
      });
    return inflightNextActions;
  }, [dashboardEnabled, platformLoading, uiDashboardEnabled]);

  const refresh = useCallback(
    () => {
      if (!dashboardEnabled || !uiDashboardEnabled || platformLoading) {
        setLoadingMetrics(false);
        setLoadingNextActions(false);
        setMetrics(null);
        setNextActions([]);
        setError(null);
        return Promise.resolve([null, []]);
      }
      if (cachedAuthError) {
        return Promise.reject(cachedError || new Error("AUTH_REQUIRED"));
      }
      return Promise.all([
        fetchMetrics({ force: true }),
        fetchNextActions({ force: true }),
      ]);
    },
    [dashboardEnabled, fetchMetrics, fetchNextActions, platformLoading, uiDashboardEnabled]
  );

  const value = useMemo(
    () => ({
      metrics,
      nextActions,
      error,
      loadingMetrics,
      loadingNextActions,
      fetchMetrics,
      fetchNextActions,
      refresh,
    }),
    [
      metrics,
      nextActions,
      error,
      loadingMetrics,
      loadingNextActions,
      fetchMetrics,
      fetchNextActions,
      refresh,
    ]
  );

  return <DashboardContext.Provider value={value}>{children}</DashboardContext.Provider>;
}

export const useDashboard = () => {
  const context = useContext(DashboardContext);
  if (!context) {
    throw new Error("useDashboard must be used within DashboardProvider");
  }
  return context;
};

export const clearDashboardCache = () => {
  cachedMetrics = null;
  cachedNextActions = null;
  cachedError = null;
  inflightMetrics = null;
  inflightNextActions = null;
  cachedAuthError = false;
};
